import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { WidgetManifest, WidgetVersionsIndex } from '@/models/widget';
import { widgetVersionPath } from '@/utils/widgetVersions';

/**
 * Absolute base URL of the widgets directory (note the trailing `/widgets/`).
 * Each version lives in a subdirectory of it, and its module entry — the one
 * script the preview loads and the snippet embeds — resolves against that.
 *
 * This must keep pointing at the portal origin even in dev: a widget bundle
 * derives its API bridge origin from its own URL and skips the bridge when that
 * origin equals the page's, so serving a bundle from this app's origin would
 * silently disable its API access. The portal origin is set per mode in the
 * repo root `.env.<mode>` files; `versions.json` and the manifests are read
 * from here too, cross-origin (the widgets distribution sends
 * `access-control-allow-origin: *`).
 */
export const WIDGET_BASE_URL = `${import.meta.env.VITE_WIDGET_ORIGIN}/widgets/`;

export const widgetManifestApi = createApi({
    reducerPath: 'widgetManifestApi',
    baseQuery: fetchBaseQuery({ baseUrl: '' }),
    endpoints: (builder) => ({
        getWidgetVersions: builder.query<WidgetVersionsIndex, void>({
            query: () => `${WIDGET_BASE_URL}versions.json`,
        }),
        getWidgetManifest: builder.query<WidgetManifest, string>({
            query: (version) =>
                widgetVersionPath(WIDGET_BASE_URL, version, 'manifest.json'),
        }),
    }),
});

export const { useGetWidgetVersionsQuery, useGetWidgetManifestQuery } =
    widgetManifestApi;
